<template>
  <div v-if="record">
    <h4 class="title text-capitalize mb-2">
      {{
        record.recordType === 'datetime' ? 'Date/Time' :
          record.recordType === 'radio' ? 'Single Choice' :
            record.recordType === 'dropdown' ? 'Dropdown - Single Selection' :
              record.recordType === 'multiple_choice' ? 'Dropdown - Multiple Selection' :
                record.recordType === 'textarea' ? 'Text' :
                  record.recordType
      }}
    </h4>

    <p v-if="record.recordType === 'image'" class="mt-4 mb-0 datasheet-record-edit-mode-file-max-size">
      Maximum size of the file is 10 MB.
    </p>

    <div class="my-2" v-if="record.recordType !== 'organism'">
      <label class="form-label text-lg">Label <span class="text-danger font-weight-bold">*</span></label>
      <input class="form-control" v-model="record.label" placeholder="Enter your question" />
    </div>

    <div v-if="(record.recordType === 'textarea' || record.recordType === 'text')">
      <div class="form-group mt-0">
        <label class="form-label">Type: </label>
        <div class="d-inline-flex ml-1">
          <div class="form-check mr-3">
            <input type="radio" v-model="record.recordType" value="text" class="form-check-input" id="recordTypeText" />
            <label for="recordTypeText" class="form-check-label">
              Single line
            </label>
          </div>
          <div class="form-check">
            <input type="radio" v-model="record.recordType" value="textarea" class="form-check-input"
              id="recordTypeTextarea" />
            <label for="recordTypeTextarea" class="form-check-label">
              Multiline
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="my-2" v-if="record.recordType !== 'organism'">
      <label class="form-label text-lg">Hint</label>
      <textarea class="form-control" v-model="record.description"
        placeholder="Provide a hint on how to answer this question" rows="2" style="resize: none;"></textarea>
    </div>

    <div v-if="record.recordType === 'dropdown' || record.recordType === 'radio'"
      class="my-3 datasheet-record-dropdown-choices">
      <label class="form-label text-lg">Choices (Select the default choice)</label>
      <VueDraggable v-model="record.optionsSet.optionsValues" @update="updateRecordOptions" :options="{
        handle: '.draggable-handler',
        animation: 200
      }">
        <div v-for="option in record.optionsSet.optionsValues" :key="option.orderNumber"
          class="datasheet-record-option-value d-flex align-items-center mb-2">

          <div class="form-check mr-2">
            <input type="radio" v-model="record.defaultOption" :value="option.orderNumber" class="form-check-input"
              :id="`defaultOption_${option.orderNumber}`" title="Click to make this Choice the Default Selection" />
            <label :for="`defaultOption_${option.orderNumber}`" class="form-check-label visually-hidden mr-1">
            </label>
          </div>

          <input class="form-control flex-grow-1 mr-3" v-model="option.value" type="text" />

          <div class="d-flex align-items-center">
            <button type="button" class="btn btn-link p-1 me-1" @click="addRecordOption(option.orderNumber)"
              title="Click to add a Choice below">
              <i class="fa fa-plus-circle text-success fa-lg"></i>
            </button>

            <button type="button" class="btn btn-link p-1 me-1" @click="removeRecordOption(option.orderNumber)"
              title="Click to remove this Choice">
              <i class="fa fa-minus-circle text-secondary fa-lg"></i>
            </button>

            <button type="button" class="btn btn-link p-1 cursor-move draggable-handler"
              title="Use this gripper to drag and re-order the Choices">
              <i class="fa fa-grip-vertical text-secondary fa-lg"></i>
            </button>
          </div>
        </div>
      </VueDraggable>
    </div>
    <div v-if="record.recordType === 'multiple_choice'" class="my-3">
      <label class="form-label text-lg">Choices (Select the default choices)</label>
      <VueDraggable v-model="record.optionsSet.optionsValues" @update="updateRecordOptions" :options="{
        handle: '.draggable-handler',
        animation: 200
      }">
        <div v-for="option in record.optionsSet.optionsValues" :key="option.orderNumber"
          class="datasheet-record-option-value d-flex align-items-center mb-2">

          <div class="form-check mr-2 mb-3">
            <input type="checkbox" v-model="record.defaultOptions" :value="option.orderNumber" class="form-check-input"
              :id="`option_${option.orderNumber}`" title="Click to make this Choice a Default Selection" />
          </div>

          <input class="form-control flex-grow-1 mr-3" v-model="option.value" type="text" />

          <div class="d-flex align-items-center">
            <button type="button" class="btn btn-link p-1 me-1" @click="addRecordOption(option.orderNumber)"
              title="Click to add a Choice below">
              <i class="fa fa-plus-circle text-success fa-lg"></i>
            </button>

            <button type="button" class="btn btn-link p-1 me-1" @click="removeRecordOption(option.orderNumber)"
              title="Click to remove this Choice">
              <i class="fa fa-minus-circle text-secondary fa-lg"></i>
            </button>

            <button type="button" class="btn btn-link p-1 cursor-move draggable-handler"
              title="Use this gripper to drag and re-order the Choices">
              <i class="fa fa-grip-vertical text-secondary fa-lg"></i>
            </button>
          </div>
        </div>
      </VueDraggable>
    </div>

    <b-form-checkbox v-if="record.recordType !== 'description'" class="my-3" v-model="record.allowReplica" id="allowReplica">
      Allow volunteer to replicate on datasheet
    </b-form-checkbox>

    <div v-if="(record.recordType === 'textarea' || record.recordType === 'text')" class="my-2">
      <label class="form-label text-lg">Default Value</label>
      <input class="form-control" v-model="record.defaultValue" placeholder="Default answer" />
    </div>

    <div v-if="record.recordType !== 'organism' && record.recordType !== 'description'">
      <p class="mt-4 mb-0 text-lg">Validation</p>
      <b-form-checkbox class="my-3" v-model="isRequired" id="isRequired">
        This is a required question
      </b-form-checkbox>
    </div>

    <!-- Min and Max for Text field-->
    <div v-if="(record.recordType === 'textarea' || record.recordType === 'text')">
      <b-form-checkbox class="mb-3" v-model="customLength" id="customLength">
        Set Min./Max Character count
      </b-form-checkbox>
      <div class="input-group mb-2">
        <div class="input-group-prepend">
          <span class="input-group-text">Min.</span>
        </div>
        <input class="form-control" v-model="min" type="text" :disabled="!customLength" />
      </div>

      <div class="input-group mb-2">
        <div class="input-group-prepend">
          <span class="input-group-text">Max.</span>
        </div>
        <input class="form-control" v-model="max" type="text" :disabled="!customLength" />
      </div>
    </div>

    <!-- Min and Max for Numeric field-->
    <div v-if="record.recordType === 'number'">
      <b-form-checkbox class="mb-3" v-model="customLength" id="customLength">
        Set Min./Max. Numeric Value
      </b-form-checkbox>

      <div class="input-group mb-2">
        <div class="input-group-prepend">
          <span class="input-group-text">Min.</span>
        </div>
        <input class="form-control" v-model="min" type="text" :disabled="!customLength" />
      </div>

      <div class="input-group mb-2">
        <div class="input-group-prepend">
          <span class="input-group-text">Max.</span>
        </div>
        <input class="form-control" v-model="max" type="text" :disabled="!customLength" />
      </div>
    </div>

    <!-- Private Responses -->
    <div class="my-4" v-if="record.recordType !== 'description'">
      <div v-if="record.recordType !== 'organism'">
        <p>Question Response Privacy</p>
        <span v-if="privateDatasheet || privateOrganism || record.isPrivate">
          <font-awesome-icon class="mr-2" :icon="['fas', 'lock']" size="2xl" /> <b>Managers and Observer Only</b>
        </span>
        <span v-else-if="privateProject">
          <font-awesome-icon class="mr-2" :icon="['fas', 'people-group']" size="2xl" /> <b>Members Only</b>
        </span>
        <span v-else>
          <font-awesome-icon class="mr-2" :icon="['fas', 'globe']" size="2xl" /> <b>Public</b>
        </span>
        <b-button v-b-modal.question-privacy-modal class="ml-3" :disabled="privateDatasheet || privateOrganism">
          <font-awesome-icon class="mr-2" :icon="['fas', 'pencil']" />
          Edit
        </b-button>
        <PrivateQuestionModal @changeQuestionToPrivate="record.isPrivate = true"
          @changeQuestionToPublic="record.isPrivate = false" :project-privacy="privateProject"
          :question-privacy="record.isPrivate" />
      </div>
      <div v-else>
        <p>Organism Response Privacy</p>
        <span v-if="privateDatasheet || record.isPrivate">
          <font-awesome-icon class="mr-2" :icon="['fas', 'lock']" size="2xl" /> <b>Managers and Observer Only</b>
        </span>
        <span v-else-if="privateProject">
          <font-awesome-icon class="mr-2" :icon="['fas', 'people-group']" size="2xl" /> <b>Members Only</b>
        </span>
        <span v-else>
          <font-awesome-icon class="mr-2" :icon="['fas', 'globe']" size="2xl" /> <b>Public</b>
        </span>
        <b-button v-b-modal.organism-privacy-modal class="ml-3" :disabled="privateDatasheet">
          <font-awesome-icon class="mr-2" :icon="['fas', 'pencil']" />
          Edit
        </b-button>
        <PrivateOrganismModal @changeOrganismToPrivate="changeOrganismToPrivate"
          @changeOrganismToPublic="changeOrganismToPublic" :project-privacy="privateProject"
          :organism-privacy="record.isPrivate" />
      </div>
      <div class="mb-4">
        <div v-if="privateDatasheet">
          The Datasheet this question belongs to is set to 'Managers and Observer Only'.
          This question's response privacy cannot be modified.
        </div>
        <div v-else-if="privateOrganism">
          The Organism this question belongs to is set to 'Managers and Observer Only'. This question's response privacy
          cannot be modified.
        </div>
      </div>
    </div>

    <OrganismSelect v-if="record.recordType === 'organism'" v-bind:record="record"
      v-on:updateRecord="updateRecord($event)">
    </OrganismSelect>
  </div>
</template>

<script>
import VueDraggable from "vuedraggable";
import OrganismSelect from "./OrganismSelect";
import PrivateOrganismModal from "./privacyModals/PrivateOrganismModal.vue";
import PrivateQuestionModal from "./privacyModals/PrivateQuestionModal.vue";

export default {
  components: {
    VueDraggable,
    OrganismSelect,
    PrivateOrganismModal,
    PrivateQuestionModal,
  },
  props: {
    record: {
      type: Object,
      default: () => {
      },
    },
    privateProject: {
      type: Boolean,
      required: true,
    },
    privateDatasheet: {
      type: Boolean,
      required: true,
    },
    privateOrganism: {
      type: Boolean,
      required: true,
      default: false,
    },
  },

  data() {
    return {
      customLength: this.record.validators.some((v) => {
        return v.validatorType === "min" || v.validatorType === "max";
      }),
      organismPrivacyModalShown: false,
    };
  },

  computed: {
    isRequired: {
      get() {
        return this.record.validators.some((v) => {
          return v.validatorType === "isRequired";
        });
      },
      set(value) {
        let validators = this.record.validators
        if (value) {
          let requiredValidator = this.record.validators.filter((validator) => {
            return validator.validatorType === "isRequired";
          });

          if (requiredValidator.length === 1) {
            // Existing validator present, value is changed
            requiredValidator[0].value = value;
            validators = validators.filter((validator) => {
              return validator.validatorType !== "isRequired";
            });
            validators.push(requiredValidator[0]);
          } else {
            // Add a new validator
            validators = [
              ...validators,
              {
                validatorType: "isRequired",
                value: value,
              },
            ];
          }
        } else {
          validators = validators.filter((validator) => {
            return validator.validatorType !== "isRequired";
          });
        }
        this.record.validators = validators;
        this.$emit("updateRecord", this.record);
      },
    },
    min: {
      get() {
        const validator = this.record.validators.find((v) => {
          return v.validatorType === "min";
        });
        return validator ? validator.value : "";
      },
      set(value) {
        if (value) {
          let validators = this.record.validators
          let minValidator = this.record.validators.filter((validator) => {
            return validator.validatorType === "min";
          });

          if (minValidator.length === 1) {
            // Existing validator present, value is changed
            minValidator[0].value = value;
            validators = validators.filter((validator) => {
              return validator.validatorType !== "min";
            });
            validators.push(minValidator[0]);
          } else {
            // Add a new validator
            validators = [
              ...validators,
              {
                validatorType: "min",
                value: value,
              },
            ];
          }
          this.record.validators = validators;
          this.$emit("updateRecord", this.record);
        }
      },
    },
    max: {
      get() {
        const validator = this.record.validators.find((v) => {
          return v.validatorType === "max";
        });
        return validator ? validator.value : "";
      },
      set(value) {
        if (value) {
          let validators = this.record.validators
          let maxValidator = this.record.validators.filter((validator) => {
            return validator.validatorType === "max";
          });

          if (maxValidator.length === 1) {
            // Existing validator present, value is changed
            maxValidator[0].value = value;
            validators = validators.filter((validator) => {
              return validator.validatorType !== "max";
            });
            validators.push(maxValidator[0]);
          } else {
            // Add a new validator
            validators = [
              ...validators,
              {
                validatorType: "max",
                value: value,
              },
            ];
          }
          this.record.validators = validators;
          this.$emit("updateRecord", this.record);
        }
      },
    },
  },

  methods: {
    updateRecord(record) {
      this.$emit("updateRecord", record);
    },
    setIsRequired(value) {
    },
    setMin(value) {
    },
    removeMin() {
    },
    setMax(value) {
    },
    removeMax() {
    },

    addOption(value) {
    },
    removeOption(value) {
    },
    updateOptions(value) {
    },

    //updateRecordAllowReplica="onUpdateRecordAllowReplica"
    //isDatasheetRecordEdited="isDatasheetRecordEdited"
    //addRecordOption="onAddRecordOption"
    //removeRecordOption="onRemoveRecordOption"
    //updateRecordOptions="onUpdateRecordOptions"
    //updateRecordRequiredValidator="onUpdateRecordRequiredValidator"
    //updateRecordIntegerValidator="onUpdateRecordIntegerValidator"
    //updateRecordDefaultOptionValue="onUpdateRecordDefaultOptionValue"
    //updateRecordMinLengthValidator="onUpdateRecordMinLengthValidator"
    //updateRecordMaxLengthValidator="onUpdateRecordMaxLengthValidator"
    setCustomLength(value) {
      this.customLength = value;
      if (!value) {
        this.min = false;
        this.max = false;
      } else {
        this.min = "0";
        this.max = "100";
      }
    },

    addRecordOption(orderNumber) {
      this.record.optionsSet.optionsValues.splice(orderNumber, 0, {
        "value": "Choice " + (this.record.optionsSet.optionsValues.length + 1),
        "dataType": "text",
        "orderNumber": orderNumber + 1
      })

      const defaultOption = this.$_.find(this.record.optionsSet.optionsValues, value => {
        return value.isDefault
      })

      if (defaultOption) {
        const defaultOptionOrderNumber = defaultOption.orderNumber

        if (orderNumber < defaultOptionOrderNumber) {
          this.dRecordObject.defaultOption = defaultOption.orderNumber.orderNumber + 1
        }
      }

      let valueOrderNumber = 1
      this.record.optionsSet.optionsValues.forEach(value => {
        value.orderNumber = valueOrderNumber++
      })

      this.isEditedValue = true

    },

    removeRecordOption(orderNumber) {
      if (this.record.optionsSet.optionsValues[orderNumber - 1].isDefault) {
        this.dRecordObject.defaultOption = null
      }

      this.record.optionsSet.optionsValues.splice(orderNumber - 1, 1)

      const defaultOption = this.$_.find(this.record.optionsSet.optionsValues, value => {
        return value.isDefault
      })

      if (defaultOption) {
        const defaultOptionOrderNumber = defaultOption.orderNumber

        if (orderNumber < defaultOptionOrderNumber) {
          this.dRecordObject.defaultOption = defaultOption.orderNumber - 1
        }
      }

      let valueOrderNumber = 1
      this.record.optionsSet.optionsValues.forEach(value => {
        value.orderNumber = valueOrderNumber++
      })
      this.isEditedValue = true
    },
    chooseDefault(defaultIndex) {
      this.record.optionsSet.optionsValues = this.record.optionsSet.optionsValues.map((option) => {
        return {
          ...option,
          isDefault: (defaultIndex === option.orderNumber)
        }
      })
      this.$emit("updateRecord", this.record);
    },
    updateRecordOptions(event) {
      // update order
      // this is triggered by any update to the list, including add / remove
      this.record.optionsSet.optionsValues = this.renumberOptions(this.record.optionsSet.optionsValues);
    },
    renumberOptions(options) {
      return options.map((option, index) => {
        return {
          ...option,
          orderNumber: index + 1
        }
      })
    },
    changeOrganismToPublic(cascadePublic) {
      this.record.isPrivate = false
      if (cascadePublic) {
        this.record.records.forEach(rec => {
          rec.isPrivate = false
        })
      }
    },
    changeOrganismToPrivate() {
      this.record.isPrivate = true
      this.record.records.forEach(rec => {
        rec.isPrivate = true
      })
    },
  },
  watch: {
    'record.defaultOption': function (value) {
      console.log('RecordEdit -> defaultOptionWatch', value)
      this.chooseDefault(value);
    },
  },
  created() {
  },
};
</script>
<style scoped>
.v-icon {
  margin-top: 12px;
}

.datasheet-record-edit-mode-file-max-size {
  color: #bc7610;
}
</style>
